/**
 * ---------------------------------------------------------------------------
 * Curated pack CTAs
 * ---------------------------------------------------------------------------
 * Powers the two CTAs on the curated-pack landing page (sections/curated-pack-lp.liquid).
 *
 * There are two visitors, and they want opposite things:
 *
 *   Guest / non-subscriber
 *     Primary   -> straight to checkout. Builds the pack as a Recharge dynamic
 *                  bundle, expands it into cart line items, POSTs /cart/add.js,
 *                  redirects to /checkout. This is exactly what
 *                  assets/pack-builder-cart.js does when someone finishes a
 *                  pack, minus the builder UI — a plain /cart/{variant}
 *                  permalink produces line items Recharge does not recognise
 *                  as a bundle.
 *     Secondary -> the pack builder, prefilled (plain href, no JS needed).
 *
 *   Active subscriber
 *     Primary   -> their next delivery. Resolves their active pack
 *                  subscription through the Recharge SDK and hands off to the
 *                  existing edit-your-pack flow with the curated slate applied,
 *                  scrolled to the update button. The customer presses Update
 *                  themselves — this script never writes to a live
 *                  subscription.
 *     Secondary -> the same edit flow, without the scroll, so they land with
 *                  the catalog in view.
 *
 * Every failure path falls back to the anchor's own href, so a CTA is never a
 * dead button.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  function split(value, sep) {
    return (value || "").split(sep).filter(function (part) {
      return part !== "";
    });
  }

  function track(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
    } catch (e) {
      /* analytics must never break a CTA */
    }
  }

  function CuratedPackCta(root) {
    this.root = root;
    this.bundlePid = root.getAttribute("data-cpl-bundle-pid") || "";
    this.bundleVid = root.getAttribute("data-cpl-bundle-vid") || "";
    this.bundleHandle = root.getAttribute("data-cpl-bundle-handle") || "salad-pack-builder";
    this.freqWeeks = Number(root.getAttribute("data-cpl-freq-weeks")) || 1;
    this.isSubscriber = root.getAttribute("data-cpl-subscriber") === "true";
    this.directCheckout = root.getAttribute("data-cpl-direct") === "true";
    this.subscriberAccessUrl =
      root.getAttribute("data-cpl-subscriber-access-url") ||
      "/tools/recurring/get-subscription-access";
    this.cdnCache = null;
    this.editBaseCache = null;
  }

  /* ---------------------------------------------------------------------
     Shared: the Recharge CDN payload (guest checkout only).
     --------------------------------------------------------------------- */
  CuratedPackCta.prototype.loadRechargeData = function () {
    if (this.cdnCache) return this.cdnCache;

    var pid = this.bundlePid;
    this.cdnCache = Promise.all([
      recharge.cdn.getCDNProductsAndSettings(),
      recharge.cdn.getCDNBundleSettings(pid),
    ]).then(function (results) {
      var list = results[0] && (results[0].products || results[0]);
      var byId = {};
      (list || []).forEach(function (entry) {
        Object.keys(entry || {}).forEach(function (key) {
          byId[key] = entry[key];
        });
      });

      var allowed = {};
      ((results[1] && results[1].variants) || []).forEach(function (variant) {
        (variant.option_sources || []).forEach(function (source) {
          allowed[String(source.collection_id)] = true;
        });
      });

      return { products: byId, collections: allowed };
    });

    return this.cdnCache;
  };

  /* Match the delivery frequency the same way pack-builder-cart.js does. */
  CuratedPackCta.prototype.pickSellingPlan = function (product) {
    var weeks = this.freqWeeks;
    var match = null;

    ((product && product.selling_plan_groups) || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (plan) {
        if (match) return;
        if (
          Number(plan.order_interval_frequency) === Number(weeks) &&
          String(plan.order_interval_unit_type) === "week"
        ) {
          match = plan.selling_plan_id;
        }
      });
    });

    return match;
  };

  CuratedPackCta.prototype.packLineKeys = function (cart) {
    var handle = this.bundleHandle;
    return ((cart && cart.items) || [])
      .filter(function (item) {
        return item.properties && item.properties._rc_bundle_parent === handle;
      })
      .map(function (item) {
        return item.key;
      });
  };

  /* ---------------------------------------------------------------------
     Guest: pack -> Recharge dynamic bundle -> cart -> checkout.
     --------------------------------------------------------------------- */
  CuratedPackCta.prototype.checkoutWithPack = async function (panel) {
    var pids = split(panel.getAttribute("data-cpl-pids"), ",");
    var vids = split(panel.getAttribute("data-cpl-vids"), ",");
    var cids = split(panel.getAttribute("data-cpl-cids"), ";");

    var items = pids.map(function (pid, index) {
      return { p: pid, v: vids[index], c: split(cids[index], "|") };
    });

    if (!items.length) throw new Error("no items on panel");
    if (!window.recharge || !recharge.bundle || !recharge.cdn) {
      throw new Error("recharge sdk missing");
    }

    var data = await this.loadRechargeData();
    var self = this;

    var selections = items.map(function (item) {
      var product = data.products[String(item.p)];
      var plan = self.pickSellingPlan(product);
      var collectionId = (item.c || []).map(String).filter(function (id) {
        return data.collections[id];
      })[0];

      if (!plan) throw new Error("no selling plan for product " + item.p);
      if (!collectionId) throw new Error("product " + item.p + " is not in a bundle collection");

      return {
        collectionId: String(collectionId),
        externalProductId: String(item.p),
        externalVariantId: String(item.v),
        quantity: 1,
        sellingPlan: plan,
      };
    });

    var bundle = {
      externalVariantId: String(this.bundleVid),
      externalProductId: String(this.bundlePid),
      selections: selections,
    };

    var valid = await recharge.bundle.validateDynamicBundle(bundle);
    if (!valid) throw new Error("bundle failed validation");

    var lineItems = await recharge.bundle.getDynamicBundleItems(bundle, this.bundleHandle);
    if (!lineItems || !lineItems.length) throw new Error("no line items returned");

    /* The pack's cup points, not its item count — a pack can hold 8 physical
       cups (four of them halves) and still be a 6-pack. */
    var packSize = Number(panel.getAttribute("data-cpl-cups")) || 6;
    lineItems.forEach(function (item) {
      item.properties = item.properties || {};
      item.properties._Pack_Size = packSize;
    });

    /* Replace any pack already in the cart, the way the builder does, so nobody
       ends up checking out with two packs. */
    var cart = await fetch(window.Shopify.routes.root + "cart.js", {
      headers: { Accept: "application/json" },
    }).then(function (response) {
      return response.json();
    });

    var stale = this.packLineKeys(cart);
    if (stale.length) {
      var updates = {};
      stale.forEach(function (key) {
        updates[key] = 0;
      });
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: updates }),
      });
    }

    var response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: lineItems }),
    });
    if (!response.ok) throw new Error("cart/add failed: " + response.status);

    window.location.href = window.Shopify.routes.root + "checkout";
  };

  /* ---------------------------------------------------------------------
     Subscriber: resolve the edit-your-pack URL for their active pack.

     The edit flow is keyed on a Recharge subscription id AND its bundle
     selection id, neither of which Liquid can know, so they are resolved here
     the same way account.js resolves them. pack_size is deliberately NOT
     passed: pack-builder.js recomputes it from the subscription's own
     selection when it applies the preset, which is more reliable than
     anything this page could infer.
     --------------------------------------------------------------------- */
  CuratedPackCta.prototype.resolveEditBase = function () {
    if (this.editBaseCache) return this.editBaseCache;

    this.editBaseCache = (async function () {
      if (!window.recharge || !recharge.auth || !recharge.subscription) {
        throw new Error("recharge sdk missing");
      }

      var session = await recharge.auth.loginShopifyAppProxy();
      if (!session || session.customerId == null) throw new Error("no recharge session");

      var response = await recharge.subscription.listSubscriptions(session, {
        include: ["bundle_product", "bundle_selections"],
      });

      var candidates = ((response && response.subscriptions) || [])
        .filter(function (subscription) {
          return (
            subscription.status === "active" &&
            subscription.include &&
            subscription.include.bundle_selections &&
            subscription.include.bundle_selections.id
          );
        })
        /* Soonest upcoming charge first: with more than one active pack, "my
           next delivery" means the one shipping next. */
        .sort(function (a, b) {
          var aDate = a.next_charge_scheduled_at || "";
          var bDate = b.next_charge_scheduled_at || "";
          if (aDate === bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return aDate < bDate ? -1 : 1;
        });

      var subscription = candidates[0];
      if (!subscription) throw new Error("no editable pack subscription");

      var selection = subscription.include.bundle_selections;
      var bundleProduct = subscription.include && subscription.include.bundle_product;
      var handle = (bundleProduct && bundleProduct.handle) || "salad-pack-builder";

      return {
        handle: handle,
        subscriptionId: String(subscription.id),
        selectionId: String(selection.id),
        nextChargeAt: subscription.next_charge_scheduled_at || "",
      };
    })();

    return this.editBaseCache;
  };

  CuratedPackCta.prototype.goToNextDelivery = async function (panel, focusSummary) {
    var param = panel.getAttribute("data-cpl-pack");
    if (!param) throw new Error("panel has no preset param");

    var base = await this.resolveEditBase();

    var params = new URLSearchParams({
      edit_bundle: base.subscriptionId,
      selection: base.selectionId,
      preset: param,
    });
    // every pack edits on the salad-pack-builder edit-your-pack template
    params.set("view", "edit-your-pack");
    if (focusSummary) params.set("preset_focus", "summary");

    window.location.href = "/products/" + base.handle + "?" + params.toString();
  };

  /* ---------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------- */
  CuratedPackCta.prototype.bind = function () {
    var self = this;

    this.root.addEventListener("click", function (event) {
      var cta =
        event.target.closest && event.target.closest("[data-cpl-cta], [data-cpl-secondary]");
      if (!cta) return;

      /* Both CTAs live inside a pack's modal, which is the element carrying the
         pack's data attributes. */
      var panel =
        cta.closest("[data-cpl-panel]") || self.root.querySelector("[data-cpl-panel].is-open");
      if (!panel) return;

      var param = panel.getAttribute("data-cpl-pack") || "";
      var isSecondary = cta.hasAttribute("data-cpl-secondary");

      if (self.isSubscriber) {
        /* Both CTAs go to the edit flow; only the scroll target differs. */
        event.preventDefault();
        cta.classList.add("is-busy");
        track("curated_pack_cta", {
          pack: param,
          mode: "next_delivery",
          cta: isSecondary ? "secondary" : "primary",
        });

        self.goToNextDelivery(panel, !isSecondary).catch(function (error) {
          console.warn("[curated-packs] next-delivery handoff failed:", error);

          /* No Recharge session means they are signed in to Shopify but have
             not been through the Recharge proxy — send them there, and bring
             them back to this pack afterwards. */
          if (String(error && error.message) === "no recharge session") {
            window.location.href = self.subscriberAccessUrl;
            return;
          }
          window.location.href = cta.getAttribute("href") || "/account";
        });
        return;
      }

      /* Guests: the secondary CTA is a plain link to the prefilled builder. */
      if (isSecondary) return;
      if (!self.directCheckout) return;
      if (!panel.getAttribute("data-cpl-pids")) return;

      event.preventDefault();
      cta.classList.add("is-busy");
      track("curated_pack_cta", { pack: param, mode: "direct_checkout", cta: "primary" });

      self.checkoutWithPack(panel).catch(function (error) {
        console.warn("[curated-packs] direct checkout failed, falling back to builder:", error);
        window.location.href =
          panel.getAttribute("data-cpl-builder-href") || cta.getAttribute("href");
      });
    });

    /* Warm the slow call for whichever path this visitor is on, so the click
       is not waiting on a network round trip. */
    var warm = function () {
      try {
        if (self.isSubscriber) self.resolveEditBase().catch(function () {});
        else if (self.directCheckout) self.loadRechargeData().catch(function () {});
      } catch (e) {
        /* ignore */
      }
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 5000 });
    else window.addEventListener("load", warm);

    /* Coming back from checkout restores this page from the back/forward cache
       with the DOM exactly as it was left — including is-busy, which sets
       pointer-events:none and would leave the button dead. pageshow is the only
       event that fires on a bfcache restore. */
    window.addEventListener("pageshow", function () {
      Array.prototype.forEach.call(self.root.querySelectorAll(".is-busy"), function (element) {
        element.classList.remove("is-busy");
      });
    });
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-cpl-root]"), function (root) {
      if (root.dataset.cplBound === "true") return;
      root.dataset.cplBound = "true";
      new CuratedPackCta(root).bind();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener("shopify:section:load", init);
})();
